import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import {
  ButtonLoadingProps,
  StyledButtonProps,
  TButtonSize,
  TButtonVariant,
} from './types';
import { TrevorIcon } from '../TrevorIcon';
import { Button } from './Button';

const sizeSmallCSSVars = css`
  --l-font-size: 12px;
  --l-line-height: var(--spacing-l);
  --l-height: var(--spacing-xl);
  --l-padding: var(--spacing-s);
  --l-gap: calc(var(--spacing-xxs) * 2);
  --l-icon-size: var(--spacing-l);
`;

const sizeMediumCSSVars = css`
  --l-font-size: 14px;
  --l-line-height: var(--spacing-l);
  --l-height: 40px;
  --l-padding: var(--spacing-m);
  --l-gap: calc(var(--spacing-xxs) * 2);
  --l-icon-size: var(--spacing-l);
`;

const sizeLargeCSSVars = css`
  --l-font-size: 18px;
  --l-line-height: 24px;
  --l-height: 56px;
  --l-padding: var(--spacing-l);
  --l-gap: var(--spacing-s);
  --l-icon-size: 24px;
`;

const variantPrimaryCSSVars = css`
  --l-color: var(--color-interface-secondary-normal);
  --l-background-color: var(--color-interface-primary-normal);
  --l-background-color-hover: var(--color-interface-primary-lighter);
  --l-border-color: var(--color-interface-primary-normal);
  --l-border-color-hover: var(--color-interface-primary-lighter);
`;

const variantSecondaryCSSVars = css`
  --l-color: var(--color-interface-secondary-lighter);
  --l-background-color: var(--color-interface-neutral-light);
  --l-background-color-hover: var(--color-interface-neutral-light);
  --l-border-color: var(--color-interface-layout-normal);
  --l-border-color-hover: var(--color-interface-secondary-lighter);
`;

const variantDangerCSSVars = css`
  --l-color: var(--color-interface-neutral-light);
  --l-background-color: var(--color-feedback-error-darkest);
  --l-background-color-hover: var(--color-feedback-error-darker);
  --l-border-color: var(--color-feedback-error-darkest);
  --l-border-color-hover: var(--color-feedback-error-darker);
`;

const variantCssVars: Record<TButtonVariant, FlattenSimpleInterpolation> = {
  primary: variantPrimaryCSSVars,
  secondary: variantSecondaryCSSVars,
  danger: variantDangerCSSVars,
};

const sizeCssVars: Record<TButtonSize, FlattenSimpleInterpolation> = {
  small: sizeSmallCSSVars,
  medium: sizeMediumCSSVars,
  large: sizeLargeCSSVars,
};

export const ButtonContainer = styled.button<StyledButtonProps>`
  ${(p) => variantCssVars[p.variant as TButtonVariant]}
  ${(p) => sizeCssVars[p.size as TButtonSize]}

  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  font-weight: 500;
  border-radius: var(--radius-100);
  width: ${(p) =>
    p.expanded ? '100%' : p.iconOnly ? 'var(--l-height)' : 'fit-content'};
  position: relative;
  transition: var(--transition-fast);
  font-size: var(--l-font-size);
  line-height: var(--l-line-height);
  height: var(--l-height);
  padding: var(--l-padding);

  color: var(--l-color);
  background-color: var(--l-background-color);
  border: 1px solid var(--l-border-color);

  > span {
    display: flex;
    justify-content: center;
    align-items: center;
    white-space: nowrap;
    gap: var(--l-gap);

    > svg,
    > img {
      width: var(--l-icon-size);
      height: var(--l-icon-size);
    }
  }

  &:hover,
  &:focus {
    box-shadow: 0 1px var(--spacing-xxs) rgba(28, 28, 33, 0.3);
    border-color: var(--l-border-color-hover);
    background: var(--l-background-color-hover);
    border: 1px solid var(--l-border-color-hover);
  }

  ${(p) =>
    p.active &&
    css`
      box-shadow: 0 1px var(--spacing-xxs) rgba(28, 28, 33, 0.3);
      border-color: var(--l-border-color-hover);
      background: var(--l-background-color-hover);
      border: 1px solid var(--l-border-color-hover);
    `}
  &:active {
    box-shadow: none;
  }

  &:disabled {
    pointer-events: none;
    border: 1px solid var(--color-interface-layout-normal);
    color: var(--color-interface-layout-darkest);
    background: var(--color-interface-layout-lighter);

    > span > * {
      opacity: 0.5;
    }
  }
`;

export const ButtonLoadingIcon = styled(TrevorIcon).attrs({ name: 'spinner' })`
  position: absolute;
  width: var(--spacing-s);
  height: var(--spacing-s);
  animation: loading 3s linear infinite;
  color: inherit;
  visibility: visible;
  margin: auto;
  @keyframes loading {
    0% {
      transform: rotate(0);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;
